import React from 'react'
import { CBadge } from '@coreui/react-pro'
import { RecordSelect, matchRecord } from './clubstatsShared'

// What makes grades, teams and players different inside LinkImporter.

const gradeLabel = (g) => g.grade

export const gradeConfig = {
  noun: 'grade',
  nounPlural: 'grades',
  recordsKey: 'our_grades',
  loadingText: 'Loading grades from ClubStats…',
  intro:
    'Already made your comps? Choose "Link to existing (keep my name)" and pick yours - nothing is renamed or created; your grade just learns which ClubStats grade it is. "Link & rename" also renames yours to ClubStats’ name. Grades come from your club’s fixtures this season.',
  getRecordName: (r) => r.grade,
  getRecordLabel: gradeLabel,
  itemName: (item) => item.name,
  itemClubstatsId: (item) => item.clubstats_id,
  itemColumns: [{ header: 'Your club’s teams', cell: (row) => row.item.teams.join(', ') }],
  extraColumns: [],
  initialExtra: () => ({}),
  toPayload: (row) => ({
    clubstats_id: row.item.clubstats_id,
    name: row.item.name,
    action: row.action,
    grade_id: row.target_id || null,
  }),
}

const teamLabel = (t) => `${t.name}${t.grade ? ` (${t.grade})` : ''}`

export const teamConfig = {
  noun: 'team',
  nounPlural: 'teams',
  recordsKey: 'our_teams',
  loadingText: 'Loading teams from ClubStats…',
  intro:
    'Already made your teams? Choose "Link to existing (keep my name)" and pick yours - nothing is renamed or created, and the Players, Fixtures and Squads tabs then pick your team for that ClubStats team. Link your grades first and each team is suggested from its grade. "Create new" adds a new team from ClubStats.',
  getRecordName: (r) => r.name,
  getRecordLabel: teamLabel,
  itemName: (item) => item.name,
  itemClubstatsId: (item) => item.clubstats_id,
  itemColumns: [{ header: 'ClubStats grade', cell: (row) => row.item.grade_name }],
  // Ticked rows can be given a grade at once - used when they are created or renamed.
  bulkFields: [
    {
      key: 'grade_id',
      label: 'Grade',
      placeholder: 'Set grade…',
      recordsKey: 'our_grades',
      getLabel: gradeLabel,
      appliesTo: (row) => row.action === 'create' || row.action === 'update',
    },
  ],
  extraColumns: [
    {
      header: 'Grade',
      cell: (row, update, data) =>
        row.action === 'create' || row.action === 'update' ? (
          <RecordSelect
            value={row.grade_id}
            onChange={(grade_id) => update({ grade_id })}
            records={data.our_grades}
            getLabel={gradeLabel}
            placeholder={row.action === 'update' ? 'Keep current grade' : 'Choose grade…'}
            invalid={row.action === 'create' && !row.grade_id}
          />
        ) : (
          <span className="small text-medium-emphasis">Unchanged</span>
        ),
    },
  ],
  // The grade linked to this ClubStats grade, else one with the same name.
  initialExtra: (item, data) => {
    const found = matchRecord(data.our_grades || [], item.grade_id, item.grade_name, (g) => g.grade)
    return { grade_id: found.record ? String(found.record.id) : '' }
  },
  // A ClubStats team in one of your grades (linked, or the same name) is your only team in that
  // grade not already taken by another row: SMCC 1st XI → your "1st XI" in the same comp.
  suggest: (item, data, ours, claimed) => {
    const grade = matchRecord(
      data.our_grades || [],
      item.grade_id,
      item.grade_name,
      (g) => g.grade,
    ).record
    if (!grade) return null
    const inGrade = ours.filter(
      (t) => String(t.grade_id) === String(grade.id) && !claimed.has(String(t.id)),
    )
    return inGrade.length === 1 ? inGrade[0] : null
  },
  rowProblem: (row) =>
    row.action === 'create' && !row.grade_id ? 'Choose a grade for the new team.' : null,
  describeNew: (row, data) => {
    const grade = (data.our_grades || []).find((g) => String(g.id) === String(row.grade_id))
    return `${row.item.name}${grade ? ` (${grade.grade})` : ''}`
  },
  toPayload: (row) => ({
    clubstats_id: row.item.clubstats_id,
    name: row.item.name,
    action: row.action,
    team_id: row.target_id || null,
    grade_id: row.grade_id || null,
  }),
}

const playerLabel = (p) =>
  `${p.full_name}${p.team_name ? ` · ${p.team_name}` : ''}${
    Number(p.is_active) === 0 ? ' (inactive)' : ''
  }`

// A price as the game stores it, e.g. 9.00 - the currency symbol is set per game.
const formatPrice = (price) => Number(price || 0).toFixed(2)

export const playerConfig = {
  noun: 'player',
  nounPlural: 'players',
  recordsKey: 'our_players',
  analyticValueToggle: true,
  loadingText:
    'Loading your club’s roster from ClubStats - it works out every player’s role, so this can take about half a minute…',
  intro:
    'Players are your club’s roster in ClubStats this season. Link the ones already in your game so future imports find them by ClubStats id; create the rest.',
  reviewNote:
    'New players take ClubStats’ suggested position, and start on your game’s price nearest ClubStats’ starting value. Linking or renaming an existing player keeps their position and price. Check low-confidence positions on the Players page afterwards.',
  getRecordName: (r) => r.full_name,
  getRecordLabel: playerLabel,
  itemName: (item) => item.name,
  itemClubstatsId: (item) => item.public_id,
  itemColumns: [
    {
      header: 'Position & value',
      cell: (row) => (
        <>
          <div className="d-flex gap-1 align-items-center flex-wrap">
            <CBadge color="dark" className="fw-normal">
              {row.item.position_label}
            </CBadge>
            {/low/i.test(row.item.position_confidence) && (
              <CBadge color="warning" className="fw-normal" title={row.item.position_confidence}>
                Low confidence
              </CBadge>
            )}
          </div>
          <div className="small text-medium-emphasis mt-1">
            Value {row.item.value} → price {formatPrice(row.item.price)}
          </div>
        </>
      ),
    },
    { header: 'Most played', cell: (row) => <span className="small">{row.item.grade_name}</span> },
  ],
  bulkFields: [
    {
      key: 'team_id',
      label: 'Team',
      placeholder: 'Set team…',
      recordsKey: 'our_teams',
      getLabel: teamLabel,
      appliesTo: (row) => row.action === 'create' || row.action === 'update',
    },
  ],
  extraColumns: [
    {
      header: 'Team',
      cell: (row, update, data) =>
        row.action === 'create' || row.action === 'update' ? (
          <RecordSelect
            value={row.team_id}
            onChange={(team_id) => update({ team_id })}
            records={data.our_teams}
            getLabel={teamLabel}
            placeholder={row.action === 'update' ? 'Keep current team' : 'No team'}
          />
        ) : (
          <span className="small text-medium-emphasis">Unchanged</span>
        ),
    },
  ],
  initialExtra: (item) => ({
    team_id: item.suggested_team_id ? String(item.suggested_team_id) : '',
  }),
  describeNew: (row, data) => {
    const team = (data.our_teams || []).find((t) => String(t.id) === String(row.team_id))
    return `${row.item.name} · ${row.item.position_label} · ${formatPrice(row.item.price)}${
      team ? ` · ${team.name}` : ''
    }`
  },
  toPayload: (row) => ({
    public_id: row.item.public_id,
    first_name: row.item.first_name,
    last_name: row.item.last_name,
    position: row.item.position,
    value: row.item.value,
    action: row.action,
    player_id: row.target_id || null,
    team_id: row.team_id || null,
  }),
}
